/**
 * Auth Flow Manager
 *
 * Handles authentication flow for the Quickstart experience.
 * Manages auth provider selection, auth attempts, and post-auth transitions.
 *
 * This is feature-specific to Quickstart and knows about:
 * - UTM-based auth provider selection
 * - Quickstart-specific auth flow
 * - Post-auth thank you page
 */

import { getAuthProviderFromUTM } from '../types/theme';
import type { QuickstartState } from '../QuickstartController';

export interface AuthFlowManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Transition to a new phase
   */
  transitionTo: (phase: string) => void;

  /**
   * Transition to thank you page (with auto-redirect check)
   */
  transitionToThankYou: () => void;

  /**
   * Send analytics events
   */
  sendAuthAttempted: () => void;
  sendAuthFailed: (provider: string, reason: string) => void;
  sendAuthSucceeded: (provider: string, playerAuthId: string, attemptId?: string, accountState?: 'signup' | 'login') => void;
  sendThankYouPageViewed: (playerAuthId: string) => void;

  /**
   * Send wishlist email (notification + analytics)
   */
  sendWishlistEmail: (playerAuthId: string, email: string, persistentId: string) => void;
}

/**
 * Manager for authentication flow
 */
export class AuthFlowManager {
  private config: AuthFlowManagerConfig;

  constructor(config: AuthFlowManagerConfig) {
    this.config = config;
  }

  /**
   * Start authentication flow
   * Determines auth provider from UTM params and initiates auth
   */
  public startAuthFlow(): void {
    const state = this.config.getState();

    // CRITICAL: Store sessionId before OAuth redirect
    // This will be picked up by constructor when returning from OAuth
    sessionStorage.setItem('quickstart_session_id', state.sessionId);
    console.log('[AuthFlowManager] Stored sessionId for OAuth resume:', state.sessionId);

    // Determine auth provider from UTM params (pass theme for fallback)
    // Theme should always be loaded when auth flow starts, but handle undefined for type safety
    const authProvider = getAuthProviderFromUTM(state.utmParams, state.theme || undefined);

    console.log('[AuthFlowManager] Starting auth flow with provider:', authProvider, 'utm_source:', state.utmParams.utm_source);

    this.config.updateState({
      authProvider,
      authAttempted: true,
    });

    this.config.transitionTo('auth_attempted');
    this.config.sendAuthAttempted();
  }

  /**
   * Handle successful authentication
   */
  public onAuthSuccess(playerAuthId: string, email?: string, persistentId?: string): void {
    this.onAuthSuccessNoTransition(playerAuthId, email, persistentId);
    this.config.transitionToThankYou();
  }

  /**
   * Handle auth success: updates state and sends analytics,
   * but does NOT transition. The caller (coordinator) handles routing.
   */
  public onAuthSuccessNoTransition(playerAuthId: string, email?: string, persistentId?: string): void {
    const state = this.config.getState();
    const authProvider = state.authProvider || 'unknown';

    console.log('[AuthFlowManager] Auth success:', playerAuthId, 'current phase:', state.phase);

    sessionStorage.removeItem('quickstart_session_id');
    console.log('[AuthFlowManager] OAuth complete, cleared sessionStorage');

    this.config.updateState({
      playerAuthId,
    });

    this.config.sendAuthSucceeded(authProvider, playerAuthId);

    if (state.theme && state.theme.sendWishlistEmail !== false && email && persistentId) {
      console.log('[AuthFlowManager] Sending wishlist email for:', playerAuthId);
      this.config.sendWishlistEmail(playerAuthId, email, persistentId);
    } else {
      console.log('[AuthFlowManager] Skipping wishlist email:', {
        enabled: state.theme?.sendWishlistEmail !== false,
        hasEmail: !!email,
        hasPersistentId: !!persistentId,
      });
    }
  }

  /**
   * Handle failed authentication
   */
  public onAuthFailure(reason: string): void {
    this.onAuthFailureNoTransition(reason);
    this.config.transitionTo('auth_failed');
  }

  /**
   * Handle auth failure: updates state and sends analytics,
   * but does NOT transition. The caller (coordinator) handles routing.
   */
  public onAuthFailureNoTransition(reason: string): void {
    const state = this.config.getState();
    const authProvider = state.authProvider || 'unknown';

    console.log('[AuthFlowManager] Auth failed:', reason);

    sessionStorage.removeItem('quickstart_session_id');
    console.log('[AuthFlowManager] OAuth failed, cleared sessionStorage');

    this.config.updateState({
      errorMessage: reason,
    });

    this.config.sendAuthFailed(authProvider, reason);
  }

  /**
   * No cleanup needed for auth flow
   */
  public cleanup(): void {
    // No-op
  }
}

